import type { BoResourceEndpointMap } from '@Common/types/bo/resource_endpoint_map';
import type { BoExternalResourceEndpointMap } from '@Common/types/bo_external/resource_endpoint_map';

import { buildSearchParams } from '@sh4res/bo-common/utils/build_link';

import type { fetchUtils } from 'react-admin';

export type EndpointResource = keyof BoResourceEndpointMap | keyof BoExternalResourceEndpointMap;

type EndpointAction = 'list' | 'show' | 'create' | 'edit' | 'delete';

export type ActionsOf<R extends EndpointResource> =
  | (R extends keyof BoResourceEndpointMap ? keyof BoResourceEndpointMap[R] : never)
  | (R extends keyof BoExternalResourceEndpointMap
      ? keyof BoExternalResourceEndpointMap[R]
      : never);

type ActionEndpointFor<R extends EndpointResource, A extends EndpointAction> =
  | (R extends keyof BoResourceEndpointMap
      ? BoResourceEndpointMap[R] extends Record<A, infer Endpoint>
        ? Endpoint
        : never
      : never)
  | (R extends keyof BoExternalResourceEndpointMap
      ? BoExternalResourceEndpointMap[R] extends Record<A, infer Endpoint>
        ? Endpoint
        : never
      : never);

type ServerSideOf<Endpoint> = Endpoint extends { Server: infer Server } ? Server : never;

type PathParamsOf<Endpoint> =
  ServerSideOf<Endpoint> extends { RequestPathParams: infer Params }
    ? Exclude<Params, undefined>
    : never;
type PathIdOf<Endpoint> =
  PathParamsOf<Endpoint> extends Record<string, unknown>
    ? keyof PathParamsOf<Endpoint> extends never
      ? never
      : PathParamsOf<Endpoint>[keyof PathParamsOf<Endpoint>]
    : never;

type HasPathId<Endpoint> = [PathIdOf<Endpoint>] extends [never] ? false : true;

type QueryOf<Endpoint> =
  ServerSideOf<Endpoint> extends { RequestQueryParams: infer Query }
    ? Exclude<Query, undefined>
    : never;

type EndpointParams<Endpoint> = (HasPathId<Endpoint> extends true
  ? { id: PathIdOf<Endpoint> }
  : { id?: never }) & {
  query?: QueryOf<Endpoint>;
};

export type BuiltEndpoint<R extends EndpointResource, A extends ActionsOf<R>> = {
  resource: R;
  action: A;
  uri: string;
};

type BoEndpoint<R, A> = R extends keyof BoResourceEndpointMap
  ? A extends keyof BoResourceEndpointMap[R]
    ? BoResourceEndpointMap[R][A]
    : never
  : R extends keyof BoExternalResourceEndpointMap
    ? A extends keyof BoExternalResourceEndpointMap[R]
      ? BoExternalResourceEndpointMap[R][A]
      : never
    : never;

type ClientResponse<R extends EndpointResource, A extends ActionsOf<R>> =
  BoEndpoint<R, A> extends { Client: { ResponseBody: infer BodyResponse } } ? BodyResponse : never;

export type InternalFetchResult<R extends EndpointResource, A extends ActionsOf<R>> = Omit<
  Awaited<ReturnType<typeof fetchUtils.fetchJson>>,
  'json'
> & {
  json: ClientResponse<R, A>;
};

type FetchJson = (
  url: string,
  options?: fetchUtils.Options,
) => ReturnType<typeof fetchUtils.fetchJson>;

export type HttpClient = <R extends EndpointResource, A extends ActionsOf<R>>(
  endpoint: BuiltEndpoint<R, A>,
  options?: fetchUtils.Options,
) => Promise<InternalFetchResult<R, A>>;

/**
 * HttpClient narrowed to one resource/action, e.g. to type a jest mock of httpClient.
 */
export type HttpClientFor<R extends EndpointResource, A extends ActionsOf<R>> = (
  endpoint: BuiltEndpoint<R, A>,
  options?: fetchUtils.Options,
) => Promise<InternalFetchResult<R, A>>;

/**
 * wraps an app's raw fetcher into a client that takes a buildEndpoint result, so the response
 * json is inferred from the endpoint's resource/action.
 */
export const makeHttpClient =
  (fetchJson: FetchJson): HttpClient =>
  async (endpoint, options = {}) =>
    fetchJson(endpoint.uri, options);

/**
 * Builds a raw backend API path for a resource, for use with httpClient. Resource/action pairs
 * are typed against BoResourceEndpointMap/BoExternalResourceEndpointMap, so a renamed or removed
 * backend route fails to compile instead of failing at runtime.
 */
export function buildEndpoint<R extends EndpointResource, A extends ActionsOf<R> & EndpointAction>(
  resource: R,
  action: A,
  ...args: HasPathId<ActionEndpointFor<R, A>> extends true
    ? [params: EndpointParams<ActionEndpointFor<R, A>>]
    : [params?: EndpointParams<ActionEndpointFor<R, A>>]
): BuiltEndpoint<R, A> {
  const [params] = args;
  const id = (params as { id?: string | number } | undefined)?.id;
  const query = (
    params as { query?: Record<string, string | number | boolean | undefined> } | undefined
  )?.query;
  const search = query ? buildSearchParams(query) : '';

  return {
    resource,
    action,
    uri: `/${resource}${id !== undefined ? `/${id}` : ''}${search ? `?${search}` : ''}`,
  };
}
